import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IBreed, IDogClubRegistration } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { dogHeightTypes, dogRallyHeights, dogObedienceHeights, dogAgilityHeights, ckcObedienceHeights, ckcRallyHeights } from "src/services/helper.service";

import Input from "../Input/Input";
import OptionButtons from "../OptionButtons/OptionButtons";
import Dropdown, { IOption } from "../Dropdown/Dropdown";
import SearchDown from "../SearchDown/SearchDown";
import Checkbox from "../Checkbox/Checkbox";

import star from "../../assets/icons/star.svg";
import num from "../../assets/icons/num.svg";

import './ClubRegistration.scss';

interface IProps {
  clubRegistration: IDogClubRegistration;
  persisted: boolean;
  onChange: (updatedRegistration: IDogClubRegistration) => void;
  onRemove: () => void;
  breeds: IBreed[];
  registrationTypes: Record<string, IOption[]>;
  breedsLoading?: boolean;
}

const ClubRegistration: React.FC<IProps> = ({ 
  clubRegistration,
  persisted,
  onChange,
  onRemove, 
  breeds,
  registrationTypes,
  breedsLoading = false
}) => {
  const [breedSearch, setBreedSearch] = useState<string>(clubRegistration.breed || "");
  const [selectedBreed, setSelectedBreed] = useState<IBreed | null>(null);

  // Get breeds for the specific sanctioning club
  const clubBreeds = useMemo(() => breeds || [], [breeds]);

  const updateField = useCallback((field: keyof IDogClubRegistration, value?: string | number | boolean) => {
    if (typeof value === "number" && isNaN(value)) return;

    onChange({
      ...clubRegistration,
      [field]: value
    });
  }, [clubRegistration, onChange]);

  // Auto-set registration_type to "ABI" for ABI clubs
  useEffect(() => {
    if (clubRegistration.sanctioning_club === Constants.sanctioning_club.ABI && !clubRegistration.registration_type) {
      updateField('registration_type', Constants.sanctioning_club.ABI);
    }
  }, [clubRegistration.sanctioning_club, clubRegistration.registration_type, updateField]);

  // Auto-set registration_type to "barn_hunt" for BHA clubs
  useEffect(() => {
    if (clubRegistration.sanctioning_club === Constants.sanctioning_club.BHA && !clubRegistration.registration_type) {
      updateField('registration_type', Constants.registration_number_type.barn_hunt);
    }
  }, [clubRegistration.sanctioning_club, clubRegistration.registration_type, updateField]);

  // Auto-set registration_type to "ASFA" for ASFA clubs
  useEffect(() => {
    if (clubRegistration.sanctioning_club === Constants.sanctioning_club.ASFA && !clubRegistration.registration_type) {
      updateField('registration_type', Constants.registration_number_type.asfa_registered);
    }
  }, [clubRegistration.sanctioning_club, clubRegistration.registration_type, updateField]);

  // When UKC registration type changes to TL, ensure number starts with "TL"
  useEffect(() => {
    if (
      clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC &&
      clubRegistration.registration_type === Constants.registration_number_type.ukc_temporary
    ) {
      const current = clubRegistration.registration_number || "";
      if (!current.startsWith("TL")) {
        updateField('registration_number', "TL" + current);
      }
    }
  }, [clubRegistration.sanctioning_club, clubRegistration.registration_type, clubRegistration.registration_number, updateField]);

  // Handle UKC TL registration number change — enforce "TL" prefix cannot be removed
  const handleTLRegNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val.startsWith("TL")) {
      // User tried to remove the prefix — restore it
      const withoutPrefix = val.replace(/^T?L?/, "");
      updateField('registration_number', "TL" + withoutPrefix);
    } else {
      updateField('registration_number', val);
    }
  };

  // Consolidated breed selection handler - only called when user clicks from dropdown
  // Must update breed/variety/variation in a single onChange to avoid stale-closure overwriting
  const handleBreedSelection = useCallback((breedName: string) => {
    const matchedBreed = clubBreeds.find(b => 
      b.breed.toLowerCase() === breedName.trim().toLowerCase()
    );
    if (!matchedBreed) return; // Should not happen when selecting from our list
    const canonicalBreed = matchedBreed.breed; // Use canonical form from database
    setBreedSearch(canonicalBreed);
    setSelectedBreed(matchedBreed);
    onChange({
      ...clubRegistration,
      breed: canonicalBreed,
      variety: '',
      variation: ''
    });
  }, [clubBreeds, clubRegistration, onChange]);

  // General and BHA allow free-text custom breeds that aren't in the list (saved to the dog profile only).
  const allowCustomBreed = [
    Constants.sanctioning_club.GENERAL,
    Constants.sanctioning_club.BHA,
  ].includes(clubRegistration.sanctioning_club);

  // Handle search input changes - typing only filters. Breed is set ONLY via onSelect (handleBreedSelection),
  // except for General where a typed custom value is persisted when it doesn't match the list.
  const handleBreedSearchChange = useCallback((searchTerm: string) => {
    setBreedSearch(searchTerm);
    const exactMatch = clubBreeds.find(b => b.breed.toLowerCase() === searchTerm.trim().toLowerCase());
    const matchesCurrentSelection = selectedBreed && searchTerm.trim().toLowerCase() === selectedBreed.breed.toLowerCase();

    if (exactMatch && matchesCurrentSelection) {
      // User typed what they already selected - keep selection (e.g. re-focus after blur)
      return;
    }
    if (exactMatch) {
      // Typed a match but didn't select - show in UI but DON'T save (user must click)
      setSelectedBreed(exactMatch);
      return;
    }
    // No list match
    setSelectedBreed(null);
    if (allowCustomBreed) {
      // General: persist the typed custom breed (no variety/variation for custom breeds)
      const customBreed = searchTerm.trim();
      if (customBreed !== (clubRegistration.breed || '')) {
        onChange({
          ...clubRegistration,
          breed: customBreed,
          variety: '',
          variation: ''
        });
      }
      return;
    }
    // Other clubs: clear (single onChange to avoid stale-closure overwriting)
    if (clubRegistration.breed) {
      onChange({
        ...clubRegistration,
        breed: '',
        variety: '',
        variation: ''
      });
    }
  }, [allowCustomBreed, clubBreeds, clubRegistration, onChange, selectedBreed]);

  const getVarietyOptions = (): IOption[] => {
    if (!selectedBreed?.variety || selectedBreed.variety.length === 0) {
      return [{ id: "None", value: "None" }];
    }
    
    // Find club-specific varieties, falling back to UKC
    let clubVarieties = selectedBreed.variety.filter(v => 
      v.name === clubRegistration.sanctioning_club
    );
    
    if (clubVarieties.length === 0) {
      clubVarieties = selectedBreed.variety.filter(v => v.name === "UKC");
    }
    
    if (clubVarieties.length === 0) {
      return [{ id: "None", value: "None" }];
    }
    
    return [
      { id: "None", value: "None" },
      ...(clubVarieties[0].values?.map((v: string) => ({ id: v, value: v })) || [])
    ];
  };

  const getVariationOptions = (): IOption[] => {
    if (!selectedBreed?.variations || selectedBreed.variations.length === 0) {
      return [{ id: "None", value: "None" }];
    }
    
    // Find club-specific variations, falling back to UKC
    let clubVariations = selectedBreed.variations.filter(v => 
      v.name === clubRegistration.sanctioning_club
    );
    
    if (clubVariations.length === 0) {
      clubVariations = selectedBreed.variations.filter(v => v.name === "UKC");
    }
    
    if (clubVariations.length === 0) {
      return [{ id: "None", value: "None" }];
    }
    
    return [
      { id: "None", value: "None" },
      ...(clubVariations[0].values?.map((v: string) => ({ id: v, value: v })) || [])
    ];
  };

  // Helper function to check if varieties are available (club-specific or UKC fallback)
  const hasVarieties = (): boolean => {
    if (!selectedBreed?.variety || selectedBreed.variety.length === 0) return false;
    return selectedBreed.variety.some(v => 
      v.name === clubRegistration.sanctioning_club || v.name === "UKC"
    );
  };

  // Helper function to check if variations are available (club-specific or UKC fallback)
  const hasVariations = (): boolean => {
    if (!selectedBreed?.variations || selectedBreed.variations.length === 0) return false;
    return selectedBreed.variations.some(v => 
      v.name === clubRegistration.sanctioning_club || v.name === "UKC"
    );
  };

  useEffect(() => {
    if (breedSearch) {
      const breed = clubBreeds.find(b => b.breed.toLowerCase() === breedSearch.toLowerCase());
      setSelectedBreed(breed || null);
    }
  }, [breedSearch, clubRegistration.sanctioning_club, clubBreeds]);

  // Auto-set registration type for CPE, GCR, and C-Wags
  useEffect(() => {
    if ([Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR, Constants.sanctioning_club.C_WAGS].includes(clubRegistration.sanctioning_club)) {
      if (!clubRegistration.registration_type) {
        updateField('registration_type', clubRegistration.sanctioning_club);
      }
    }
  }, [clubRegistration.sanctioning_club, clubRegistration.registration_type, updateField]);

  // Format C-WAGS registration number: 7-8 digits formatted as ##-####-#(##)
  const formatCWagsRegNum = (value: string): string => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 8 digits
    const limited = digitsOnly.slice(0, 8);
    
    // Apply format: ##-####-##
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 2)}-${limited.slice(2)}`;
    } else {
      return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6, 8)}`;
    }
  };

  // Handle C-WAGS registration number change
  const handleCWagsRegNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCWagsRegNum(e.target.value);
    // Store unformatted (digits only) in state
    const digitsOnly = formatted.replace(/\D/g, '');
    updateField('registration_number', digitsOnly);
    // Update the input display with formatted value
    e.target.value = formatted;
  };

  return (
    <div className="ClubRegistration">
      <div className="clubHeader">
        <h4>{clubRegistration.sanctioning_club} Registration</h4>
        {!persisted && <button type="button" className="removeClub" onClick={onRemove}>
          Remove
        </button>}
      </div>

      {/* Registration Section - Hide for General and 4-H sanctioning clubs */}
      {clubRegistration.sanctioning_club !== Constants.sanctioning_club.GENERAL &&
       clubRegistration.sanctioning_club !== Constants.sanctioning_club.FH && (
        <div className="registrationSection">
          <h5>Registration Information</h5>
          <div className="regNumWrap">
            {/* Only show Registration Type dropdown for clubs other than CPE, GCR, and C-Wags */}
            {![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR, Constants.sanctioning_club.C_WAGS].includes(clubRegistration.sanctioning_club) && (
            <Dropdown 
              value={{ 
                id: clubRegistration.sanctioning_club === Constants.sanctioning_club.ABI 
                  ? Constants.sanctioning_club.ABI 
                  : clubRegistration.registration_type || "", 
                value: clubRegistration.sanctioning_club === Constants.sanctioning_club.ABI 
                  ? Constants.sanctioning_club.ABI 
                  : clubRegistration.registration_type || "" 
              }}
              label="Registration Type"
              onChange={(e: any, value: IOption) => updateField('registration_type', value.value)}
              options={registrationTypes[clubRegistration.sanctioning_club] || []}
              placeholder="Select type"
              disabled={clubRegistration.sanctioning_club === Constants.sanctioning_club.ABI}
            />
          )}
          <Input 
            icon={num}
            label="Registration Number (optional)"
            type="text"
            onChange={
              clubRegistration.sanctioning_club === Constants.sanctioning_club.C_WAGS
                ? handleCWagsRegNumChange
                : clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC &&
                  clubRegistration.registration_type === Constants.registration_number_type.ukc_temporary
                ? handleTLRegNumChange
                : (e) => updateField('registration_number', e.target.value)
            }
            placeholder={
              clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC &&
              clubRegistration.registration_type === Constants.registration_number_type.ukc_temporary
                ? 'TL12345'
                : clubRegistration.sanctioning_club === Constants.sanctioning_club.BHA 
                ? 'BH-00123' 
                : clubRegistration.sanctioning_club === Constants.sanctioning_club.PWDCA 
                ? 'AKC or CKC registration number'
                : clubRegistration.sanctioning_club === Constants.sanctioning_club.C_WAGS
                ? '12-3456-7 or 12-3456-78'
                : clubRegistration.sanctioning_club === Constants.sanctioning_club.CKC
                ? `${clubRegistration.registration_type || 'CKC'} number`
                : [Constants.sanctioning_club.ABI, Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR].includes(clubRegistration.sanctioning_club)
                  ? `${clubRegistration.sanctioning_club} registration number (optional)`
                  : `${clubRegistration.sanctioning_club} registration number`
            }
            value={
              clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC &&
              clubRegistration.registration_type === Constants.registration_number_type.ukc_temporary
                ? (clubRegistration.registration_number || "TL")
                : undefined
            }
            defaultValue={
              clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC &&
              clubRegistration.registration_type === Constants.registration_number_type.ukc_temporary
                ? undefined
                : clubRegistration.sanctioning_club === Constants.sanctioning_club.C_WAGS && clubRegistration.registration_number
                ? formatCWagsRegNum(clubRegistration.registration_number)
                : clubRegistration.registration_number || ""
            }
          />
        </div>
        <div className="registrationHelpText">
          {clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC &&
           clubRegistration.registration_type === Constants.registration_number_type.ukc_temporary
            ? 'TL numbers must begin with "TL". The prefix is required and cannot be removed.'
            : clubRegistration.sanctioning_club === Constants.sanctioning_club.BHA 
            ? 'Format: BH-##### (e.g., BH-00123, BH-12345). Must have exactly 5 digits after BH- with no spaces.'
            : clubRegistration.sanctioning_club === Constants.sanctioning_club.C_WAGS
            ? 'Format: 7 or 8 digits (e.g., 12-3456-7 or 12-3456-78). Dashes will be added automatically.'
            : clubRegistration.sanctioning_club === Constants.sanctioning_club.CKC
            ? 'Conformation accepts a CKC registration number, an Event Registration Number (ERN), a Temporary Competition Number (TCN), or a Miscellaneous Certification Number (MSC) for listed breeds. Obedience, Rally Obedience and Scent Detection also accept a Performance Event Number (PEN) and a Canine Companion Number (CCN). A TCN entry pays a TCN listing fee per show or trial.'
            : 'Make sure to include any prefix letters in your registration number'
          }
        </div>
      </div>
      )}

      {/* Name Section */}
      <div className="registrationSection">
        <h5>Dog Names</h5>
        <Input 
          label="Call Name"
          type="text"
          onChange={(e) => updateField('call_name', e.target.value)}
          placeholder="Dog's call name"
          defaultValue={clubRegistration.call_name || ""}
        />
        
        {/* Only show Prefix and Suffix titles for clubs other than CPE, GCR, General, and 4-H */}
        {![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR, Constants.sanctioning_club.GENERAL, Constants.sanctioning_club.FH].includes(clubRegistration.sanctioning_club) && (
          <div className="nameFieldsSection">
            <Input 
              label="Prefix Title"
              type="text"
              onChange={(e) => updateField('prefix_title', e.target.value)}
              placeholder="Prefix title (optional)"
              defaultValue={clubRegistration.prefix_title || ""}
            />
            <Input 
              label="Suffix Title"
              type="text"
              onChange={(e) => updateField('suffix_title', e.target.value)}
              placeholder="Suffix title (optional)"
              defaultValue={clubRegistration.suffix_title || ""}
            />
          </div>
        )}

        {/* Only show Registered Name for clubs other than CPE, GCR, General, and 4-H */}
        {![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR, Constants.sanctioning_club.GENERAL, Constants.sanctioning_club.FH].includes(clubRegistration.sanctioning_club) && (
          <Input 
            label="Registered Name"
            type="text"
            onChange={(e) => updateField('registered_name', e.target.value)}
            placeholder="Official registered name"
            defaultValue={clubRegistration.registered_name || ""}
          />
        )}
      </div>

      {/* Breed Section */}
      <div className="registrationSection">
        <h5>Breed Information</h5>
        <div className="breedSection">
          {/* For CPE and GCR, use simple text input */}
          {[Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR].includes(clubRegistration.sanctioning_club) ? (
            <Input
              label={`${clubRegistration.sanctioning_club} Breed`}
              type="text"
              onChange={(e) => updateField('breed', e.target.value)}
              placeholder="Enter breed name"
              defaultValue={clubRegistration.breed || ""}
            />
          ) : (
            <SearchDown
              label={`${clubRegistration.sanctioning_club} Breed`}
              icon={star}
              type="text"
              onChange={(e) => {
                e.stopPropagation();
                handleBreedSearchChange(e.target.value);
              }}
              onSelect={(value: string) => {
                handleBreedSelection(value);
              }}
              onSearch={(searchTerm: string) => {
                handleBreedSearchChange(searchTerm);
              }}
              placeholder={breedsLoading ? "Loading breeds..." : `Select ${clubRegistration.sanctioning_club} breed`}
              searchResults={breedsLoading ? [] : clubBreeds
                .filter(b => {
                  if (!breedSearch || breedSearch.length < 1) return false;
                  return b.breed.toLowerCase().includes(breedSearch.toLowerCase());
                })
                .map(b => b.breed)
                .sort()
                .slice(0, 10) // Limit results for performance
              }
              defaultValue={breedSearch}
            />
          )}
          
          {/* Breed help text - only for non-CPE/GCR clubs */}
          {!breedsLoading && ![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR].includes(clubRegistration.sanctioning_club) && (
            <div className="breedHelpText">
                          {!selectedBreed && breedSearch.length === 0 && "Start typing to search for breeds"}
            {!selectedBreed && breedSearch.length > 0 && !allowCustomBreed && "No matching breeds found"}
            {!selectedBreed && breedSearch.length > 0 && allowCustomBreed && "Not in the list? Just type your dog's breed and it will be saved."}
            {selectedBreed && hasVarieties() && "Varieties available for this breed"}
            {selectedBreed && !hasVarieties() && "No varieties available for this breed"}
            </div>
          )}

          {/* Variety & Variation - only for non-CPE/GCR clubs */}
          {![Constants.sanctioning_club.CPE, Constants.sanctioning_club.GCR].includes(clubRegistration.sanctioning_club) && selectedBreed && (hasVarieties() || hasVariations()) && (
            <div className="varietyVariationGrid">
              {/* Variety */}
              {hasVarieties() && (
                <Dropdown
                  value={{ id: clubRegistration.variety || "None", value: clubRegistration.variety || "None" }}
                  label="Variety"
                  onChange={(e: any, value: IOption) => updateField('variety', value.value === "None" ? "" : value.value)}
                  options={getVarietyOptions()}
                  placeholder="Select variety"
                />
              )}

              {/* Variation */}
              {hasVariations() && (
                <Dropdown
                  value={{ id: clubRegistration.variation || "None", value: clubRegistration.variation || "None" }}
                  label="Variation"
                  onChange={(e: any, value: IOption) => updateField('variation', value.value === "None" ? "" : value.value)}
                  options={getVariationOptions()}
                  placeholder="Select variation"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {clubRegistration.sanctioning_club === Constants.sanctioning_club.AKC && <div className="registrationSection">
        <Checkbox
          id="dog-measured"
          label="Does this dog need to be measured at trials?"
          value={!!clubRegistration.needs_measurement}
          onChange={() => {
            updateField('needs_measurement', !clubRegistration.needs_measurement);
          }}
        />
      </div>}

      {/* Jump Heights Section */}
      {(clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC || 
        clubRegistration.sanctioning_club === Constants.sanctioning_club.AKC ||
        clubRegistration.sanctioning_club === Constants.sanctioning_club.CKC) && (
        <div className="registrationSection">
          <h5>Jump Heights</h5>
          
          {/* UKC Jump Height Type */}
          {clubRegistration.sanctioning_club === Constants.sanctioning_club.UKC && (
            <>
              <Dropdown
                value={{
                  id: clubRegistration.ukc_jump_height_type || "",
                  value: clubRegistration.ukc_jump_height_type || ""
                }}
                label="UKC Jump Height Type"
                onChange={(_e, value: IOption) => {
                  updateField('ukc_jump_height_type', value.value);
                }}
                options={dogHeightTypes}
                placeholder="Select jump height type"
              />
              <div className="jumpHeightHelpText">
                Select the appropriate jump height type for UKC events
              </div>
            </>
          )}

          {/* AKC Jump Heights */}
          {clubRegistration.sanctioning_club === Constants.sanctioning_club.AKC && (
            <>
              <div className="akcJumpHeightGrid">
                <Dropdown
                  value={{
                    id: clubRegistration.akc_obedience_jump_height?.toString() || "",
                    value: clubRegistration.akc_obedience_jump_height?.toString() || ""
                  }}
                  label="Obedience Jump Height"
                  onChange={(_e, value: IOption) => {
                    updateField('akc_obedience_jump_height', parseInt(value.value));
                  }}
                  options={dogObedienceHeights}
                  placeholder="Select obedience jump height"
                />
                <Dropdown
                  value={{
                    id: clubRegistration.akc_rally_jump_height?.toString() || "",
                    value: clubRegistration.akc_rally_jump_height?.toString() || ""
                  }}
                  label="Rally Jump Height"
                  onChange={(_e, value: IOption) => {
                    updateField('akc_rally_jump_height', parseInt(value.value));
                  }}
                  options={dogRallyHeights}
                  placeholder="Select rally jump height"
                />
                <Dropdown
                  value={{
                    id: clubRegistration.akc_obedience_preferred_jump_height?.toString() || "",
                    value: clubRegistration.akc_obedience_preferred_jump_height?.toString() || ""
                  }}
                  label="Obedience Preferred Jump Height"
                  onChange={(_e, value: IOption) => {
                    updateField('akc_obedience_preferred_jump_height', parseInt(value.value));
                  }}
                  options={dogObedienceHeights}
                  placeholder="Select obedience preferred jump height"
                />
                <Dropdown
                  value={{
                    id: clubRegistration.akc_agility_jump_height?.toString() || "",
                    value: clubRegistration.akc_agility_jump_height?.toString() || ""
                  }}
                  label="Agility Jump Height"
                  onChange={(_e, value: IOption) => {
                    updateField('akc_agility_jump_height', parseInt(value.value));
                  }}
                  options={dogAgilityHeights}
                  placeholder="Select agility jump height"
                />
              </div>
              <div className="jumpHeightHelpText">
                Set jump heights for obedience and rally events (optional)
              </div>
            </>
          )}

          {/* CKC Obedience Jump Height */}
          {clubRegistration.sanctioning_club === Constants.sanctioning_club.CKC && (
            <>
              <Dropdown
                value={{
                  id: clubRegistration.ckc_obedience_jump_height?.toString() || "",
                  value: clubRegistration.ckc_obedience_jump_height ? `${clubRegistration.ckc_obedience_jump_height}"` : ""
                }}
                label="CKC Obedience Jump Height (inches)"
                onChange={(_e, value: IOption) => {
                  updateField('ckc_obedience_jump_height', parseInt(value.id));
                }}
                options={ckcObedienceHeights}
                placeholder="Select obedience jump height"
              />
              <div className="jumpHeightHelpText">
                Whole inches, as nearly as possible your dog's height at the withers. Appendix F breeds and veterans may jump ¾ height. The judge may measure.
              </div>
              <OptionButtons
                value={{
                  id: clubRegistration.ckc_rally_jump_height?.toString() || "",
                  value: clubRegistration.ckc_rally_jump_height ? `${clubRegistration.ckc_rally_jump_height}"` : ""
                }}
                label="CKC Rally Jump Height (inches)"
                onChange={(_e, value: IOption) => {
                  updateField('ckc_rally_jump_height', parseInt(value.id));
                }}
                options={ckcRallyHeights}
              />
              <div className="jumpHeightHelpText">
                6" under 10" at the withers, 8" to under 15", 12" to under 20", 16" at 20" and over. No breed or age exceptions.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ClubRegistration; 